import { ChangeEvent, FC, useState, MouseEvent } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import {
  HStack,
  VStack,
  Image,
  Text,
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Switch,
  FormControl,
  FormErrorMessage,
  Input,
  Button,
  useDisclosure,
  Heading,
  Checkbox,
  Avatar,
  AvatarGroup,
} from '@chakra-ui/react';
import { useEffectOnce } from 'usehooks-ts';
import { useFieldArray, useForm } from 'react-hook-form';

import { DeleteModal } from '../../../components';
import { ReactComponent as DotsIcon } from '../../../assets/icons/dots.svg';
import { ReactComponent as DragHandler } from '../../../assets/forms/drag-handler.svg';
import { ReactComponent as Trash } from '../../../assets/icons/trash.svg';
import PlusOrangeIcon from '../../../assets/icons/plus-orange.svg';
import { ReactComponent as RolesAddBtnIcon } from '../../../assets/forms/roles-add-btn.svg';
import { FormFieldTypes, FormFiled } from '../../../models';
import { fieldTypeMapper, fieldIconMapper } from '../utils';
import {
  removeTemplateField,
  duplicateTemplateField,
  updateTemplateField,
} from '../form-slice';
import { useAppDispatch, useTypedSelector } from '../../../hooks';
import { useGetAllRolesQuery } from '../../../services';

export const FormField: FC<{
  field: FormFiled;
  provided: DraggableProvided;
}> = ({ field, provided }) => {
  const [checkAllWhoCanView, setCheckAllWhoCanView] = useState(
    field.whoCanView && field.whoCanView[0] === 'all'
  );
  const [checkAllWhoCanAnswer, setCheckAllWhoCanAnswer] = useState(
    field.whoCanAnswer && field.whoCanAnswer[0] === 'all'
  );
  const [isExpanded, setIsExpanded] = useState(true);
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    watch,
  } = useForm<FormFiled>({
    defaultValues: field,
  });

  const isSelectableType = [
    FormFieldTypes.singleSelection,
    FormFieldTypes.multipleSelection,
  ].includes(field.type);

  const dispatch = useAppDispatch();
  const { userDetails } = useTypedSelector(state => state.auth);

  const { data } = useGetAllRolesQuery(
    {
      basicInfoId: Number(userDetails?.companyBasicInfo.id),
      page: 0,
      size: 10000,
    },
    {
      skip: !userDetails,
    }
  );

  const allRoles = data?.data.content || [];

  const {
    fields: optionsFields,
    append: appendOption,
    remove: removeOption,
  } = useFieldArray({
    control,
    name: `options`,
    rules: {
      minLength: isSelectableType ? 1 : undefined,
      required: isSelectableType ? 'Option title is required' : false,
    },
  });

  const {
    isOpen: isDeleteFieldModalOpen,
    onOpen: onOpenDeleteFieldModal,
    onClose: onCloseDeleteFieldModal,
  } = useDisclosure();

  useEffectOnce(() => {
    if (!optionsFields.length && isSelectableType) {
      appendOption({ title: '', orderNumber: 1 });
    }
  });

  function onSubmit(data: Omit<FormFiled, 'id' | 'type'>) {
    setIsExpanded(false);

    const {
      title,
      description,
      options,
      requiredForInitiation,
      whoCanView,
      whoCanAnswer,
    } = data;

    const fieldData: Omit<FormFiled, 'orderNumber'> = {
      id: field.id,
      type: field.type,
      requiredForInitiation,
      whoCanView:
        checkAllWhoCanView || !whoCanView?.length
          ? ['all']
          : whoCanView?.filter(Boolean),
      whoCanAnswer:
        checkAllWhoCanAnswer || !whoCanAnswer?.length
          ? ['all']
          : whoCanAnswer?.filter(Boolean),
    };

    if (field.type !== FormFieldTypes.separator) {
      fieldData.title = title;
      fieldData.description = description;
    }

    if (
      [
        FormFieldTypes.singleSelection,
        FormFieldTypes.multipleSelection,
      ].includes(field.type)
    ) {
      fieldData.options = options;
    }

    dispatch(updateTemplateField({ field: fieldData }));
  }

  const handleCheckAllWhoCanViewRoles = (e: ChangeEvent<HTMLInputElement>) => {
    setCheckAllWhoCanView(e.target.checked);
  };

  const handleCheckAllWhoCanAnswerRoles = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    setCheckAllWhoCanAnswer(e.target.checked);
  };

  const stopCloseMenuItem = (e: MouseEvent<HTMLElement>) => e.stopPropagation();

  return (
    <HStack gap="0" align="baseline">
      {isDeleteFieldModalOpen && (
        <DeleteModal
          isOpen={isDeleteFieldModalOpen}
          onClose={isDeleted => {
            if (isDeleted) {
              dispatch(removeTemplateField({ id: String(field.id) }));
            }
            onCloseDeleteFieldModal();
          }}
          title="Delete This section?"
          message="Are you sure you want to delete this section? This action cannot be undone."
        />
      )}

      <Box
        {...provided.dragHandleProps}
        visibility={!isExpanded ? 'visible' : 'hidden'}
      >
        <DragHandler />
      </Box>

      <VStack
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        align="stretch"
        border="1px"
        borderColor="custom_gray.3"
        borderRadius="md"
        m="2"
        ml="0"
        p="4"
        onClick={() => setIsExpanded(true)}
        w="full"
      >
        <HStack justify="space-between" align="center" mb="10">
          <HStack>
            <Image src={fieldIconMapper(field.type)} />

            <Heading
              as="h3"
              color="custom_blue.1"
              fontSize="2xl"
              fontWeight="bold"
            >
              {fieldTypeMapper(field.type)}
            </Heading>
          </HStack>

          <HStack>
            {field.type !== FormFieldTypes.separator && (
              <>
                <Text>Required</Text>
                <Switch
                  colorScheme="orange"
                  {...register('requiredForInitiation')}
                />
              </>
            )}
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<DotsIcon />}
                aria-label="Actions role"
                bg="transparent"
              />
              <MenuList>
                <MenuItem
                  onClick={() => dispatch(duplicateTemplateField({ field }))}
                >
                  Duplicate
                </MenuItem>
                <MenuItem onClick={onOpenDeleteFieldModal}>Delete</MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </HStack>

        {isExpanded && field.type !== FormFieldTypes.separator && (
          <>
            <HStack gap="4" align="flex-start" mb="8">
              <HStack>
                <Box color="custom_orange.2" fontWeight="medium" fontSize="md">
                  Who can view?
                </Box>
                <Box>
                  <AvatarGroup size="sm" max={2}>
                    {checkAllWhoCanView ? (
                      <Avatar name="all" bg="#E8E7E3" />
                    ) : (
                      watch('whoCanView')
                        ?.filter(Boolean)
                        .map(roleId => (
                          <Avatar
                            key={roleId}
                            name={
                              allRoles.find(({ id }) => String(id) === roleId)
                                ?.name
                            }
                            bg="#E8E7E3"
                          />
                        ))
                    )}
                  </AvatarGroup>
                </Box>
                <Box>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Options"
                      icon={<RolesAddBtnIcon />}
                      variant="ghost"
                    />
                    <MenuList py="0">
                      {allRoles.map(({ id, name }) => (
                        <MenuItem key={id} py="2" onClick={stopCloseMenuItem}>
                          <HStack onClick={stopCloseMenuItem}>
                            <Checkbox
                              colorScheme="orange"
                              {...register(`whoCanView`)}
                              value={String(id)}
                            />
                            <Box fontSize="sm">{name}</Box>
                          </HStack>
                        </MenuItem>
                      ))}
                      <MenuItem py="2">
                        <HStack onClick={stopCloseMenuItem}>
                          <Checkbox
                            colorScheme="orange"
                            onChange={handleCheckAllWhoCanViewRoles}
                            isChecked={checkAllWhoCanView}
                          />
                          <Box fontSize="sm">all</Box>
                        </HStack>
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Box>
              </HStack>

              <HStack>
                <Box color="custom_orange.2" fontWeight="medium" fontSize="md">
                  Who can answer?
                </Box>
                <Box>
                  <AvatarGroup size="sm" max={2}>
                    {checkAllWhoCanAnswer ? (
                      <Avatar name="all" bg="#E8E7E3" />
                    ) : (
                      watch('whoCanAnswer')
                        ?.filter(Boolean)
                        .map(roleId => (
                          <Avatar
                            key={roleId}
                            name={
                              allRoles.find(({ id }) => String(id) === roleId)
                                ?.name
                            }
                            bg="#E8E7E3"
                          />
                        ))
                    )}
                  </AvatarGroup>
                </Box>
                <Box>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Options"
                      icon={<RolesAddBtnIcon />}
                      variant="ghost"
                    />
                    <MenuList py="0">
                      {allRoles.map(({ id, name }) => (
                        <MenuItem key={id} py="2" onClick={stopCloseMenuItem}>
                          <HStack onClick={stopCloseMenuItem}>
                            <Checkbox
                              colorScheme="orange"
                              {...register(`whoCanAnswer`)}
                              value={String(id)}
                            />
                            <Box fontSize="sm">{name}</Box>
                          </HStack>
                        </MenuItem>
                      ))}
                      <MenuItem py="2">
                        <HStack onClick={stopCloseMenuItem}>
                          <Checkbox
                            colorScheme="orange"
                            onChange={handleCheckAllWhoCanAnswerRoles}
                            isChecked={checkAllWhoCanAnswer}
                          />
                          <Box fontSize="sm">all</Box>
                        </HStack>
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Box>
              </HStack>
            </HStack>

            <HStack gap="4" align="flex-start" mb="8">
              <VStack color="custom_blue.1" align="start" flexBasis="25%">
                <Box fontSize="sm" fontWeight="semibold">
                  Title
                </Box>
              </VStack>
              <FormControl isInvalid={!!errors.title} flexBasis="50%" mt="2">
                <Input
                  placeholder="Write the section title here ..."
                  {...register('title', {
                    required:
                      'Please fill in the required input before saving this section.',
                  })}
                />
                <FormErrorMessage>
                  {errors.title && errors.title.message}
                </FormErrorMessage>
              </FormControl>
            </HStack>

            <HStack gap="4" align="flex-start" mb="8">
              <VStack color="custom_blue.1" align="start" flexBasis="25%">
                <Box fontSize="sm">
                  <Box fontWeight="semibold">Description</Box>
                  <Box as="span">(Optional)</Box>
                </Box>
              </VStack>
              <FormControl flexBasis="50%" mt="2">
                <Input
                  placeholder="Add more details for description ..."
                  {...register('description')}
                />
              </FormControl>
            </HStack>

            {isSelectableType && (
              <HStack gap="4" align="flex-start" mb="8">
                <VStack color="custom_blue.1" align="start" flexBasis="25%">
                  <Box fontSize="sm" fontWeight="semibold">
                    Choices
                  </Box>
                </VStack>

                <VStack flexBasis="50%">
                  {optionsFields.map(({ id }, index) => (
                    <FormControl
                      isInvalid={
                        !!(errors.options?.length && errors.options[index])
                      }
                      flexBasis="50%"
                      mt="2"
                      key={id}
                    >
                      <HStack>
                        <Input
                          flexBasis="90%"
                          placeholder={`Option. ${index + 1}`}
                          {...register(`options.${index}.title`, {
                            required: true,
                          })}
                        />

                        {index !== 0 && (
                          <IconButton
                            onClick={() => removeOption(index)}
                            aria-label="remove option"
                            bg="none"
                            _hover={{ bg: 'none' }}
                            icon={<Trash />}
                          />
                        )}
                      </HStack>
                      <FormErrorMessage>Choice is required</FormErrorMessage>
                    </FormControl>
                  ))}

                  <Button
                    onClick={() =>
                      appendOption({
                        orderNumber: optionsFields.length + 1,
                        title: '',
                      })
                    }
                    bg="white"
                    _hover={{ bg: 'white' }}
                    border="1px"
                    borderColor="custom_orange.2"
                    color="custom_orange.2"
                    fontWeight="semibold"
                    fontSize="sm"
                    w="full"
                  >
                    <Image
                      src={PlusOrangeIcon}
                      alt="plus icon"
                      boxSize="5"
                      mr="1"
                    />
                    Add Another Option
                  </Button>
                </VStack>
              </HStack>
            )}

            <HStack mt={4} justify="end">
              <Button onClick={() => {}}>Cancel</Button>
              <Button
                isLoading={false}
                bg="custom_orange.2"
                _hover={{ bg: 'custom_orange.2' }}
                color="white"
                type="submit"
              >
                Save
              </Button>
            </HStack>
          </>
        )}
      </VStack>
    </HStack>
  );
};
